import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Cookie notice banner (BR-3, BR-4, PC-1, PC-2). Non-modal, fixed to the bottom edge (above a
 * mobile bottom navigation via --cookie-consent-bottom-offset), with no dismiss-without-choosing
 * control. Colors come only from the --cookie-consent-* variables each app maps to its design
 * tokens (PC-3); the fallbacks are CSS system colors, never literals.
 */
import { useEffect, useRef } from 'react';
import { openCookiePreferences, setAnalyticsConsent } from './store.js';
import { useCookieConsent } from './useCookieConsent.js';
const RESERVED_HEIGHT_VAR = '--cookie-consent-height';
const COOKIE_POLICY_HREF = '/privacy-policy#cookies-and-tracking-technologies';
/** Shared consent-UI tokens (banner + dialog). */
export const cc = {
    bg: 'var(--cookie-consent-bg, Canvas)',
    fg: 'var(--cookie-consent-fg, CanvasText)',
    muted: 'var(--cookie-consent-muted, GrayText)',
    border: 'var(--cookie-consent-border, GrayText)',
    primary: 'var(--cookie-consent-primary, CanvasText)',
    primaryFg: 'var(--cookie-consent-primary-fg, Canvas)',
    scrim: 'var(--cookie-consent-scrim, color-mix(in srgb, CanvasText 50%, transparent))',
    z: 'var(--cookie-consent-z, 60)',
};
/** A 44px-tall button. Accept and Reject both use 'primary' so neither is de-emphasized (PC-1). */
export function consentButtonStyle(variant) {
    const primary = variant === 'primary';
    return {
        minHeight: '44px',
        padding: '0 16px',
        borderRadius: '8px',
        border: `1px solid ${primary ? cc.primary : cc.border}`,
        background: primary ? cc.primary : 'transparent',
        color: primary ? cc.primaryFg : cc.fg,
        font: 'inherit',
        fontSize: '14px',
        fontWeight: 600,
        lineHeight: 1.2,
        cursor: 'pointer',
    };
}
/** Focus ring for the consent UI's controls (inline styles cannot express :focus-visible). */
export function ConsentFocusStyles() {
    return (_jsx("style", { children: '.resida-cc-focus:focus-visible{outline:2px solid var(--cookie-consent-focus, Highlight);outline-offset:2px;}' }));
}
const bannerStyle = {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 'var(--cookie-consent-bottom-offset, env(safe-area-inset-bottom, 0px))',
    zIndex: cc.z,
    background: cc.bg,
    color: cc.fg,
    borderTop: `1px solid ${cc.border}`,
    padding: '16px',
    boxSizing: 'border-box',
};
const innerStyle = {
    maxWidth: '72rem',
    margin: '0 auto',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px 24px',
};
const textStyle = {
    flex: '1 1 320px',
    margin: 0,
    fontSize: '14px',
    lineHeight: 1.5,
};
const linkStyle = {
    // Inline (not inline-flex/min-height) so the 44px target is vertical padding, which grows the
    // hit area without stretching the paragraph's line boxes.
    display: 'inline',
    padding: '14px 4px',
    color: cc.fg,
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
    fontWeight: 600,
};
const actionsStyle = {
    flex: '0 1 auto',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    width: '100%',
    maxWidth: '30rem',
};
/**
 * While the banner is shown, publish its height as --cookie-consent-height on <html>. Each app
 * reserves that space inside its own layout (a body padding on document-scrolling pages; shell
 * padding and sidebar inset for the landlord app shell) so the content the banner overlaps,
 * typically the footer and its Cookie preferences link, stays reachable.
 */
function useReservePageSpace(ref) {
    useEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        const root = document.documentElement;
        const apply = () => {
            root.style.setProperty(RESERVED_HEIGHT_VAR, `${el.offsetHeight}px`);
        };
        apply();
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(apply);
        observer?.observe(el);
        return () => {
            observer?.disconnect();
            root.style.removeProperty(RESERVED_HEIGHT_VAR);
        };
    }, [ref]);
}
function Banner() {
    const ref = useRef(null);
    useReservePageSpace(ref);
    return (_jsxs("div", { ref: ref, role: "region", "aria-label": "Cookie notice", "data-testid": "cookie-banner", style: bannerStyle, children: [_jsx(ConsentFocusStyles, {}), _jsxs("div", { style: innerStyle, children: [_jsxs("p", { style: textStyle, children: ["We use cookies for analytics to understand how Resida is used and to improve it. You can accept, reject, or manage your choice at any time.", ' ', _jsx("a", { href: COOKIE_POLICY_HREF, target: "_blank", rel: "noopener", "data-testid": "cookie-banner-policy-link", className: "resida-cc-focus", style: linkStyle, children: "Privacy Policy" })] }), _jsxs("div", { style: actionsStyle, children: [_jsx("button", { type: "button", "data-testid": "cookie-banner-accept", className: "resida-cc-focus", style: { ...consentButtonStyle('primary'), flex: '1 1 96px' }, onClick: () => setAnalyticsConsent('granted', 'banner'), children: "Accept" }), _jsx("button", { type: "button", "data-testid": "cookie-banner-reject", className: "resida-cc-focus", style: { ...consentButtonStyle('primary'), flex: '1 1 96px' }, onClick: () => setAnalyticsConsent('denied', 'banner'), children: "Reject" }), _jsx("button", { type: "button", "data-testid": "cookie-banner-manage", className: "resida-cc-focus", style: { ...consentButtonStyle('secondary'), flex: '1 1 auto' }, onClick: openCookiePreferences, children: "Manage preferences" })] })] })] }));
}
export function CookieConsentBanner() {
    const consent = useCookieConsent();
    if (consent.status !== 'resolved' || !consent.bannerVisible)
        return null;
    return _jsx(Banner, {});
}
//# sourceMappingURL=CookieConsentBanner.js.map